import { useState } from 'react';
import { Popover } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import toast from 'react-hot-toast';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';

export default function CustomersTable({ 
  customers, 
  onSort, 
  sortConfig, 
  searchTerm, 
  onRefresh,
  selectedCustomers = [],
  onToggleCustomer,
  onToggleAll
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);

  // Szűrési logika a meglévő keresőmezőhöz
  const filteredCustomers = searchTerm 
    ? customers.filter(customer => {
        const search = searchTerm.toLowerCase();
        return (
          customer.name?.toLowerCase().includes(search) ||
          customer.customerId?.toLowerCase().includes(search) ||
          customer.address?.city?.toLowerCase().includes(search) ||
          customer.address?.zipCode?.toLowerCase().includes(search) ||
          customer.address?.fullAddress?.toLowerCase().includes(search) ||
          customer.address?.country?.toLowerCase().includes(search) ||
          customer.primaryContact?.firstName?.toLowerCase().includes(search) ||
          customer.primaryContact?.lastName?.toLowerCase().includes(search) ||
          customer.primaryContact?.email?.toLowerCase().includes(search) ||
          customer.primaryContact?.phone?.toLowerCase().includes(search) ||
          customer.status?.toLowerCase().includes(search)
        );
      })
    : customers;

  const handleRowClick = (firebaseId, event) => {
    // Ne navigáljon, ha a törlés gombra vagy a checkbox-ra kattintottunk
    if (event.target.closest('.delete-button') || event.target.closest('.checkbox-container')) {
      return;
    }
    navigate(`/admin/customers/${firebaseId}`);
  };

  const handleDeleteClick = (e, customer) => {
    e.stopPropagation(); // Megakadályozza a sor kattintás eseményét
    setCustomerToDelete(customer);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteDoc(doc(db, 'customers', customerToDelete.id));
      toast.success(t('customers.deleteSuccess'));
      if (typeof onRefresh === 'function') {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error(t('customers.deleteError'));
    } finally {
      setDeleteModalOpen(false);
      setCustomerToDelete(null);
    }
  };

  const handleCheckboxClick = (e, customerId) => {
    e.stopPropagation(); // Megakadályozza a sor kattintás eseményét
    if (onToggleCustomer) {
      onToggleCustomer(customerId);
    }
  };

  const handleSelectAllClick = (e) => {
    e.stopPropagation();
    if (onToggleAll) {
      onToggleAll();
    }
  };

  const isAllSelected = filteredCustomers.length > 0 && 
    filteredCustomers.every(customer => selectedCustomers.includes(customer.id));

  const renderUserName = (customer) => {
    return customer.name || customer.customerId;
  };

  const formatAddress = (address) => {
    if (!address) return '';
    const parts = [
      address.city,
      address.zipCode,
      address.fullAddress,
      address.country
    ].filter(Boolean);
    return parts.join(', ');
  };

  const renderContactInfo = (contact) => {
    if (!contact) return '';
    return [
      `${contact.lastName} ${contact.firstName}`,
      contact.email,
      contact.phone
    ].filter(Boolean).join(' • ');
  };

  const renderProjectInfo = (project) => {
    if (!project) return '';
    return (
      <div>
        <div className="text-sm font-medium text-gray-900">{project.name}</div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
            project.status === 'Feldolgozva'
              ? 'bg-green-50 text-green-600'
              : project.status === 'Módosítás szükséges'
              ? 'bg-yellow-50 text-yellow-600'
              : project.status === 'Új jelentkezés'
              ? 'bg-blue-50 text-blue-600'
              : 'bg-gray-100 text-gray-600'
          }`}>
            {project.status}
          </span>
          <span className="text-xs text-gray-400">ID: {project.id}</span>
        </div>
      </div>
    );
  };

  const columns = [
    { key: 'select', label: '', sortable: false },
    { key: 'name', label: 'Név', sortable: true },
    { key: 'address', label: 'Cím', sortable: true },
    { key: 'project', label: 'Projekt', sortable: true },
    { key: 'status', label: 'Státusz', sortable: true },
    { key: 'contact', label: 'Kapcsolattartó', sortable: false },
    { key: 'actions', label: '', sortable: false },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50/50">
              <th className="px-4 py-3 bg-gray-50 text-left w-10">
                <div className="checkbox-container flex items-center justify-center">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAllClick}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                </div>
              </th>
              {columns.slice(1).map(column => (
                <th
                  key={column.key}
                  className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredCustomers.map((customer) => (
              <tr 
                key={customer.id}
                onClick={(e) => handleRowClick(customer.id, e)}
                className={`hover:bg-gray-50/50 cursor-pointer transition-colors ${
                  selectedCustomers.includes(customer.id) ? 'bg-indigo-50/50' : ''
                }`}
              >
                <td className="whitespace-nowrap px-4 py-3 w-10">
                  <div className="checkbox-container flex items-center justify-center">
                    <input
                      type="checkbox"
                      checked={selectedCustomers.includes(customer.id)}
                      onChange={(e) => handleCheckboxClick(e, customer.id)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                  </div>
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
                      {customer.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 truncate max-w-[120px]">
                        {renderUserName(customer)}
                      </div>
                      <div className="text-xs text-gray-500 truncate max-w-[120px]">
                        ID: {customer.customerId}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="whitespace-nowrap px-4 py-3 hidden md:table-cell">
                  <div className="text-sm text-gray-600">
                    {formatAddress(customer.address)}
                  </div>
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  {renderProjectInfo(customer.project)}
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
                    customer.status === 'Pro ügyfél'
                      ? 'bg-green-50 text-green-600'
                      : customer.status === 'Régi ügyfél'
                      ? 'bg-blue-50 text-blue-600'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    <Icons.Circle className="h-2 w-2" fill="currentColor" />
                    <span className="hidden sm:inline">{customer.status}</span>
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-600">
                    {renderContactInfo(customer.primaryContact)}
                  </div>
                  {customer.secondaryContact?.firstName && (
                    <div className="text-xs text-gray-500 mt-1">
                      + {renderContactInfo(customer.secondaryContact)}
                    </div>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right">
                  <button
                    onClick={(e) => handleDeleteClick(e, customer)}
                    className="delete-button inline-flex items-center justify-center p-2 text-red-600 hover:text-red-900 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <Icons.Trash2 className="h-5 w-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {/* No Results Message */}
        {filteredCustomers.length === 0 && (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">
              Nincs találat a keresési feltételekre.
            </p>
          </div>
        )}
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('customers.deleteConfirmTitle')}
        message={t('customers.deleteConfirmMessage').replace('${name}', customerToDelete?.name || t('customers.unknownCustomer'))}
      />
    </div>
  );
}